/**
 * Nachladen nach einem Deploy (#1205, Mitarbeiter-App #1403): Wer die Website
 * oder die Mitarbeiter-App offen hat, während deployt wird, bekommt beim Nachladen eines Teils (lazy Route, `@defer`) einen
 * Ladefehler — Firebase Hosting hat die alten, gehashten Dateien entfernt.
 * Best Practice (Vite, Next.js, SvelteKit): die Seite einmal neu laden.
 */

/** Meldungen der Browser für ein fehlgeschlagenes dynamisches `import()`. */
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari (ohne Dateinamen)
  /Unable to preload CSS/i,
  /Loading chunk [\w-]+ failed/i,
];

const STORAGE_KEY = 'awo-care.chunk-reload';

/** Liefert die Fehlermeldung, wenn es ein Chunk-Ladefehler ist — auch eingepackt (Rejection, `cause`). */
export function chunkErrorMessage(error: unknown, depth = 0): string | null {
  if (!error || depth > 3) {
    return null;
  }
  if (typeof error === 'string') {
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(error)) ? error : null;
  }
  if (typeof error !== 'object') {
    return null;
  }
  const { message, rejection, cause } = error as {
    message?: unknown;
    rejection?: unknown;
    cause?: unknown;
  };
  return (
    chunkErrorMessage(message, depth + 1) ??
    chunkErrorMessage(rejection, depth + 1) ??
    chunkErrorMessage(cause, depth + 1)
  );
}

/**
 * Merkmal für den Schleifenschutz: die fehlende Datei. Nach einem neuen Deploy
 * heißt sie anders, darf also wieder neu laden. Safari nennt keine Datei — dort
 * gilt die Meldung selbst, also ein Reload pro Tab-Sitzung.
 */
export function chunkErrorKey(message: string): string {
  return message.match(/https?:\/\/\S+?\.(?:m?js|css)\b/)?.[0] ?? message;
}

/**
 * Lädt die Seite neu, wenn dieselbe Datei nicht schon einmal einen Reload
 * ausgelöst hat und niemand ein Formular angefangen hat. Fehlt sie nach dem
 * Reload wieder, ist sie wirklich kaputt — dann kein zweiter Versuch.
 * Liefert `true`, wenn neu geladen wird.
 */
export function reloadOnce(win: Window, key: string): boolean {
  if (win.document.querySelector('form.ng-dirty')) {
    return false;
  }
  try {
    const tried: string[] = JSON.parse(win.sessionStorage.getItem(STORAGE_KEY) ?? '[]');
    if (tried.includes(key)) {
      return false;
    }
    win.sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...tried, key]));
  } catch {
    // Ohne Storage (Privatmodus, blockierte Cookies) kein Schleifenschutz — dann lieber nicht neu laden.
    return false;
  }
  win.location.reload();
  return true;
}
